import { useEffect, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { Hash, Lightbulb, CheckCircle2, XCircle } from "lucide-react";

export default function JerseyChallenge() {
  const [data, setData] = useState(null);
  const [guess, setGuess] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fz.jersey.daily().then(({ data: d }) => setData(d)).catch(() => {});
  }, []);

  if (!data) return <div className="titans-card p-6 text-titans-silver text-sm">Loading…</div>;

  const submit = async (e) => {
    e.preventDefault();
    if (!guess.trim() || busy) return;
    setBusy(true);
    try {
      const { data: r } = await fz.jersey.guess(guess.trim());
      setData((d) => ({ ...d, attempts: r.attempts, completed: r.completed, hints: r.hints, answer: r.answer, solved: r.correct || d.solved }));
      if (r.correct) toast.success("Number cruncher!");
      else toast.error(r.completed ? "Out of tries!" : "Not him — here's a hint");
      if (r.award) toastAward(r.award);
      setGuess("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Guess failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="titans-card p-5 md:p-6" data-testid="fz-jersey">
      <div className="flex items-center gap-2 mb-4">
        <Hash size={16} className="text-titans-blue" />
        <span className="font-display text-xl uppercase">Jersey Number Challenge</span>
      </div>

      <div className="grid md:grid-cols-2 gap-5 items-center">
        <div className="rounded-xl h-56 flex flex-col items-center justify-center" style={{ background: "linear-gradient(180deg, #0C2340 0%, #17406E 70%, #2E6FB4 140%)" }}>
          <div className="font-cond uppercase tracking-[0.3em] text-[10px] text-white/70 mb-1">Who wore</div>
          <div className="font-display text-8xl text-white leading-none" data-testid="fz-jersey-number">#{data.number}</div>
          <div className="font-cond uppercase tracking-[0.2em] text-[10px] text-titans-red mt-2" style={{ color: "#FF5A6E" }}>Titans / Oilers legend</div>
        </div>
        <div>
          <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mb-2" data-testid="fz-jersey-attempts">
            Attempt {Math.min(data.attempts + 1, 3)} of 3 — hints unlock per miss
          </div>
          <div className="space-y-2 mb-3">
            {(data.hints || []).map((h, i) => (
              <div key={i} className="flex items-start gap-2 bg-titans-void border-l-4 border-titans-blue p-2.5" data-testid={`fz-jersey-hint-${i}`}>
                <Lightbulb size={12} className="text-titans-blue mt-0.5 shrink-0" />
                <span className="font-body text-sm">{h}</span>
              </div>
            ))}
          </div>
          {!data.completed ? (
            <form onSubmit={submit} className="flex gap-2">
              <input
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Name that player…"
                data-testid="fz-jersey-input"
                className="flex-1 bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-3 font-body text-sm"
              />
              <button type="submit" disabled={busy || !guess.trim()} data-testid="fz-jersey-submit" className="bg-titans-red hover:bg-titans-red-hover text-white px-5 py-2.5 font-cond uppercase tracking-wider text-xs disabled:opacity-50">
                Guess
              </button>
            </form>
          ) : (
            <div className={`border-l-4 p-3 ${data.solved ? "border-emerald-500 bg-emerald-500/10" : "border-titans-red bg-titans-red/10"}`} data-testid="fz-jersey-result">
              <div className="flex items-center gap-2">
                {data.solved ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-titans-red" />}
                <span className="font-display text-xl uppercase">{data.answer}</span>
              </div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-1">New number tomorrow</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
